import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useState, useEffect } from 'react';
import { LoadingSpinner } from '../modules/shared/components/LoadingSpinner';

interface RequireRoleProps {
  roles: string[];
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * Guard de ruta por rol. Si el usuario no tiene un rol permitido, muestra un
 * modal "Acceso restringido" durante 1.5s y luego redirige a `redirectTo` (`/`
 * por defecto). Si no hay usuario (sesión expirada), redirige directamente sin modal.
 *
 * Consolidado en prompt 351 — antes redirigía silenciosamente sin feedback.
 */
export function RequireRole({ roles, children, redirectTo = '/' }: RequireRoleProps) {
  const { user, loading } = useAuth();
  const [redirect, setRedirect] = useState(false);

  const denied = !loading && !!user && !roles.includes(user.role);

  useEffect(() => {
    if (!denied) return;
    const timer = window.setTimeout(() => setRedirect(true), 1500);
    return () => window.clearTimeout(timer);
  }, [denied]);

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
        }}
      >
        <LoadingSpinner size="md" muted />
      </div>
    );
  }
  if (!user) return <Navigate to={redirectTo} replace />;
  if (redirect) return <Navigate to={redirectTo} replace />;

  if (denied) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(0,0,0,0.4)',
          fontFamily: 'inherit',
        }}
      >
        <div
          style={{
            background: '#fff',
            borderRadius: 12,
            padding: '28px 36px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            textAlign: 'center',
            maxWidth: 380,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: 14,
            }}
          >
            <svg
              width={44}
              height={44}
              viewBox="0 0 24 24"
              fill="none"
              stroke="#1F3864"
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <div
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: '#1F3864',
              marginBottom: 8,
              letterSpacing: '-0.01em',
            }}
          >
            Acceso restringido
          </div>
          <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.5 }}>
            No tienes permisos para acceder a esta sección.
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
